import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { fetchWithAuth } from '@/lib/api'

const ORG_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Drop slugs / synthetic ids (e.g. "personal") before calling Drive auth APIs. */
function normalizeOrgId(organizationId?: string | null): string | undefined {
  if (!organizationId) return undefined
  return ORG_UUID_RE.test(organizationId) ? organizationId : undefined
}

export type GoogleDriveAuthStatus = {
  connected: boolean
  /** Token exists but missing full Drive scope - browse/publish will fail until reconnect. */
  needsReauth?: boolean
  organizationId?: string | null
  accountEmail?: string | null
  oauthConfigured?: boolean
  hasDriveScope?: boolean
  hasActivityScope?: boolean
  hasDocumentsScope?: boolean
}

export function useGoogleDriveAuth(organizationId?: string) {
  const [authStatus, setAuthStatus] = useState<GoogleDriveAuthStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const safeOrgId = useMemo(() => normalizeOrgId(organizationId), [organizationId])
  const failCountRef = useRef(0)

  const refetch = useCallback(async () => {
    if (failCountRef.current >= 3) return
    try {
      setIsLoading(true)
      setError(null)
      const qs = safeOrgId
        ? `?organizationId=${encodeURIComponent(safeOrgId)}`
        : ''
      const res = await fetchWithAuth(`/api/google-drive/auth/status${qs}`)
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(typeof body?.error === 'string' ? body.error : 'Failed to check Google Drive status')
      }
      failCountRef.current = 0
      const data = (await res.json()) as GoogleDriveAuthStatus
      setAuthStatus(data)
    } catch (err) {
      failCountRef.current += 1
      setError(err instanceof Error ? err.message : 'Failed to check Google Drive status')
      setAuthStatus({ connected: false, organizationId: safeOrgId ?? null })
    } finally {
      setIsLoading(false)
    }
  }, [safeOrgId])

  useEffect(() => {
    failCountRef.current = 0
    void refetch()

    const onAuthSuccess = () => {
      failCountRef.current = 0
      void refetch()
    }
    window.addEventListener('google-drive-auth-success', onAuthSuccess)

    const pollInterval = window.setInterval(() => {
      if (authStatus?.connected) return
      if (failCountRef.current >= 3) return
      void refetch()
    }, 10_000)

    return () => {
      window.clearInterval(pollInterval)
      window.removeEventListener('google-drive-auth-success', onAuthSuccess)
    }
  }, [authStatus?.connected, refetch])

  return { authStatus, isLoading, error, refetch }
}
